import { loadSprites } from '../engine/assetLoader';
import { Direction, GameState, Key } from '../engine/enums';
import { Renderable } from '../engine/renderable';
import Vector2d from '../engine/vector2d';
import Entity from '../entity';
import Blinky from '../gameObjects/blinky';
import Clyde from '../gameObjects/clyde';
import Inky from '../gameObjects/inky';
import PacMan from '../gameObjects/pacman';
import Pinky from '../gameObjects/pinky';
import Lifebar from '../lifebar';
import Maze from '../maze';
import State from '../state';
import TextRenderer, { Color, FontSize } from '../text';

import { RenderableScreen, Screen, ScreenManager } from './screenManager';

export default class GameScreen implements RenderableScreen {
  name = 'GameScreen';

  private readonly width: number;
  private readonly height: number;
  private readonly mazeCanvas: HTMLCanvasElement;
  private readonly mazeCtx: CanvasRenderingContext2D;
  private readonly screenManager: ScreenManager;

  private gameObjects: Renderable<State>[] = [];

  private textRenderer: TextRenderer | undefined;

  constructor(screenManager: ScreenManager) {
    this.screenManager = screenManager;
    this.width = screenManager.width;
    this.height = screenManager.height;

    this.mazeCanvas = document.createElement('canvas');
    this.mazeCanvas.width = this.width;
    this.mazeCanvas.height = this.height;
    this.mazeCtx = this.mazeCanvas.getContext('2d')!;
  }

  onPlayerInput(player: Entity, key: Key, state: State): void {
    if (state.gameState !== GameState.Playing) {
      return;
    }

    switch (key) {
      case Key.Up:
        player.nextDirection = Direction.Up;
        break;
      case Key.Down:
        player.nextDirection = Direction.Down;
        break;
      case Key.Left:
        player.nextDirection = Direction.Left;
        break;
      case Key.Right:
        player.nextDirection = Direction.Right;
        break;
    }
  }

  initScreen(state: State): void {
    state.reset();
    this.initLevel(state);
  }

  private initLevel(state: State): void {
    this.gameObjects = [];

    loadSprites([
      'pacman',
      'redman',
      'sprites',
      'ghosts',
      'font'
    ])
      .then(sprites => {
        this.gameObjects.push(new Maze(sprites.find(s => s.name === 'sprites')));
        this.gameObjects.push(new PacMan(sprites.find(s => s.name === 'pacman'), 0));
        this.gameObjects.push(new PacMan(sprites.find(s => s.name === 'redman'), 1));
        this.gameObjects.push(new Blinky(sprites.find(s => s.name === 'ghosts')));
        this.gameObjects.push(new Pinky(sprites.find(s => s.name === 'ghosts')));
        this.gameObjects.push(new Inky(sprites.find(s => s.name === 'ghosts'), 300));
        this.gameObjects.push(new Clyde(sprites.find(s => s.name === 'ghosts'), 300));
        this.gameObjects.push(new Lifebar(sprites.find(s => s.name === 'pacman'), 0, new Vector2d(1, 35)));
        this.gameObjects.push(new Lifebar(sprites.find(s => s.name === 'redman'), 1, new Vector2d(49, 35)));
        this.textRenderer = new TextRenderer(sprites.find(s => s.name === 'font'));

        this.update(0, state);
        this.preRenderMaze(0, this.mazeCtx);
      });
  }

  /**
   * Pre-render maze for performance
   */
  private preRenderMaze(gameTime: number, ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    this.gameObjects.filter(obj => obj.name === 'maze').forEach(obj => obj.render(gameTime, ctx));
    ctx.restore();
  }


  update(gameTime: number, state: State): void {
    if (gameTime >= 100 && state.gameState === GameState.Ready) {
      state.gameState = GameState.Playing;
    }

    if (gameTime % 2 === 0) {
      this.gameObjects.forEach(obj => obj.update(gameTime, state));
    }

    if (state.isGameOver()) {
      this.screenManager.setScreen(Screen.GameOver);
    }
  }

  render(state: State, gameTime: number, ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.drawImage(this.mazeCanvas, 0, 0);

    this.gameObjects.filter(obj => obj.name === 'maze').forEach(obj => (obj as Maze).renderFood(gameTime, ctx));

    this.textRenderer?.renderText(`P1 ${state.players[0].displayScore}`, FontSize.Normal, Color.White, new Vector2d(1, 1), ctx);
    this.textRenderer?.renderText(`P2 ${state.players[1].displayScore}`, FontSize.Normal, Color.White, new Vector2d(49, 1), ctx);
    ctx.save();

    const { gameObjects } = this;

    if (state.gameState === GameState.Playing) {
      gameObjects
        .filter(obj => obj.name !== 'maze')
        .forEach(obj => obj.render(gameTime, ctx));
    } else {
      gameObjects
        .filter(obj => obj.name !== 'maze' && obj.name !== 'ghost' && obj.name !== 'pacman')
        .forEach(obj => obj.render(gameTime, ctx));
    }

    ctx.restore();

    switch (state.gameState) {
      case GameState.Ready:
          this.textRenderer?.renderText('ready!', FontSize.Normal, Color.Yellow, new Vector2d(29.5, 18.1), ctx);
       break;
      case GameState.Playing:
        if (gameTime < 200) {
          this.textRenderer?.renderText('go!', FontSize.Normal, Color.Yellow, new Vector2d(30.5, 18.1), ctx);
        }
        break;
      case GameState.Paused:
        this.textRenderer?.renderText('paused!', FontSize.Normal, Color.Yellow, new Vector2d(29, 18.1), ctx);
        break;
    }
  }
}
